/**
 * The Mailer interface (docs/53 §1.2, docs/55 WP-NOTIFY and WP-AUTH). Senders hand over a template id and its
 * data, never finished HTML: WP-NOTIFY renders from the WP-COPY catalogue and sends through Cloudflare Email
 * Service, Postmark or the capture adapter (docs/55 §4). The auth templates are defined here first because
 * sign-in can't wait for the real mailer; WP-NOTIFY adds its own templates to `MailMessage` by adding members.
 *
 * Rules: `idempotencyKey` is stable per logical email so a retry never sends twice; messages carry what the
 * reader needs and nothing more (no lead contents, docs/53 T-20 spirit); links are absolute app URLs.
 */
import { z } from 'zod';
import { Day, Email, IsoDateTime } from './common.ts';

const AbsUrl = z.url({ protocol: /^https?$/ }).max(2048);
const Code = z.string().regex(/^\d{6}$/);

const mail = <T extends string, D extends z.ZodRawShape>(template: T, data: D) =>
  z.object({
    template: z.literal(template),
    to: Email,
    /** Stable per logical email (for example the challenge id), so a retried send is dropped by the adapter. */
    idempotencyKey: z.string().min(8).max(128),
    data: z.object(data).strict(),
  });

/** "Your sign in code: 482913" with the code and the scanner-safe link (docs/52 §7, docs/53 §4.1). */
export const SigninCodeMail = mail('auth.signin_code', { code: Code, link: AbsUrl, expiresInMinutes: z.literal(15) });

/** A fresh code to confirm it's you before a sensitive action (docs/53 §4.2 step-up). No link: codes only. */
export const StepUpCodeMail = mail('auth.step_up_code', { code: Code, expiresInMinutes: z.literal(15) });

/** "New sign in to your Found First account" with a "This wasn't me" link (docs/53 §4.1 New device). */
export const NewDeviceMail = mail('auth.new_device', {
  at: IsoDateTime,
  city: z.string().max(80).nullable(),
  device: z.string().max(120).nullable(),
  notMeLink: AbsUrl,
});

/** Sent to the new address: confirm the change (docs/53 §4.2 Email change). */
export const EmailChangeConfirmMail = mail('auth.email_change_confirm', { confirmLink: AbsUrl, effectiveAt: IsoDateTime });

/** Sent to the old address: a change is coming, with a cancel link (docs/53 §4.2). */
export const EmailChangeNoticeMail = mail('auth.email_change_notice', { maskedNewEmail: z.string().max(260), cancelLink: AbsUrl, effectiveAt: IsoDateTime });

/** A team invite (docs/50 §4.11): a 7-day link that signs the invited person in and adds them. Added by WP-API. */
export const TeamInviteMail = mail('team.invite', {
  inviteLink: AbsUrl,
  businessName: z.string().min(1).max(60),
  inviterName: z.string().max(80).nullable(),
  role: z.enum(['owner', 'manager', 'crew', 'bookkeeper']),
  expiresAt: IsoDateTime,
});

/** The cancel confirmation (docs/52 S95, deck ruling c104): the dates, and the way back while it's pending. Added by WP-API. */
export const BillingCanceledMail = mail('billing.canceled', { lastDay: Day, siteUntil: Day, exportUntil: Day, appLink: AbsUrl });

/** A pause instead of canceling (deck ruling c031). Added by WP-API. */
export const BillingPausedMail = mail('billing.paused', { from: Day, until: Day, appLink: AbsUrl });

/** "Keep my plan" undid a pending cancel (deck ruling c104). Added by WP-API. */
export const BillingKeptMail = mail('billing.kept', { appLink: AbsUrl });

export const MailMessage = z.discriminatedUnion('template', [
  SigninCodeMail,
  StepUpCodeMail,
  NewDeviceMail,
  EmailChangeConfirmMail,
  EmailChangeNoticeMail,
  TeamInviteMail,
  BillingCanceledMail,
  BillingPausedMail,
  BillingKeptMail,
]);
export type MailMessage = z.infer<typeof MailMessage>;
export type MailTemplate = MailMessage['template'];

export interface MailResult {
  /** The adapter's message id (capture adapters use the idempotency key). */
  id: string;
  /** True when the idempotency key had already been sent and nothing new went out. */
  duplicate: boolean;
}

/**
 * Implemented by WP-NOTIFY (Cloudflare Email Service, Postmark) and by the capture adapter in @ff/auth. `send`
 * parses its input with `MailMessage` and throws on a message it can't send; callers decide whether that fails
 * the request (step-up) or is logged (a new-device alert).
 */
export interface Mailer {
  send(message: MailMessage): Promise<MailResult>;
}
